<template>
  <b-form-group
    :id="id + '-group'"
    :label="label"
    :label-for="id"
    :state="state"
    :invalid-feedback="errors.first(id)"
    :label-class="labelClass"
    :class="{
      'form-input--disabled': disabled,
      'form-input--focus': isFocus
    }"
    class="form-input"
  >
    <div class="form-input__wrapper">
      <input
        :id="id"
        ref="input"
        v-model="model"
        v-validate="{ rules: validationRules }"
        :name="id"
        :placeholder="placeholder"
        :type="passwordVisible ? 'text' : inputType"
        :class="{ 'form-input__input': true, 'form-input__input--minimal': variant === 'minimal', 'form-input__input--disabled': disabled }"
        :disabled="disabled"
        :data-testing="dataTesting"
        :data-vv-validate-on="validateOnInput ? 'input|blur' : 'blur'"
        @blur="onBlur"
        @focus="onFocus"
        @input="onInput"
        @keydown.enter="onBlur"
      >
      <span
        v-if="showPasswordIcon"
        class="form-input__icon"
        @click="passwordVisible = !passwordVisible"
      />
      <!-- @slot additional: for displaying additional info (like a tooltip) -->
      <slot name="additional" />
    </div>
  </b-form-group>
</template>

<script>
import Vue from 'vue';
import { BFormGroup } from 'bootstrap-vue';

/**
 * Standard form input field
 */
export default {
  name: 'FormInput',
  components: {
    BFormGroup
  },
  inject: {
    $validator: '$validator'
  },
  props: {
    variant: {
      type: String,
      default: 'default'
    },
    /**
    * input id - it's used also as name attribute and for error handling
    */
    id: {
      type: String,
      required: true
    },
    /**
    * different input type - it sets the correct validation rules
    */
    type: {
      type: String,
      required: true
    },
    /**
    * required attribute
    */
    required: {
      type: Boolean,
      default: false
    },
    /**
    * label for the bootstrap form group
    */
    label: {
      type: String,
      default: ''
    },
    /**
    * placeholder for input field
    */
    placeholder: {
      type: String,
      default: ''
    },
    /**
    * value to fill in data from the api
    */
    value: {
      type: [Number, String],
      default: null
    },
    /**
     * custom validation rules if needed
     */
    validation: {
      type: Object,
      default: null
    },
    disabled: {
      type: Boolean,
      default: false
    },
    errorOnBlur: {
      type: Boolean,
      default: false
    },
    validateOnInput: {
      type: Boolean,
      default: false
    },
    showPasswordIcon: {
      type: Boolean,
      default: false
    },
    showRequiredAsterisk: {
      type: Boolean,
      default: false
    },
    /**
     * Variable for cypress testing
     */
    dataTesting: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      /**
       * @model
       */
      model: '',
      isFocus: false,
      passwordVisible: false
    };
  },

  computed: {
    state() {
      if (this.errorOnBlur && typeof this.fields[this.id] !== 'undefined') {
        return this.fields[this.id].touched ? !this.errors.has(this.id) : true;
      }

      return !this.errors.has(this.id);
    },
    /**
     * Returns the type of input field.
     */
    inputType() {
      switch (this.type) {
        case 'amount':
          return 'text';
        case 'zip':
          return 'number';
        case 'date':
          return 'text';
        default:
          return this.type;
      }
    },

    /**
     * Sets the validationRules, according to the type of the input field.
     */
    validationRules() {
      if (this.validation !== null) return this.validation;

      let rules = {};

      if (this.required) {
        Vue.set(rules, 'required', true);
      }

      switch (this.type) {
        case 'email':
          Vue.set(rules, 'email', true);
          break;
        case 'date':
          Vue.set(rules, 'date_format', 'd.M.yyyy');
          break;
        case 'zip':
          Vue.set(rules, 'max', 6);
      }

      return rules;
    },

    /**
     * Set label class
     */
    labelClass() {
      return this.showRequiredAsterisk ? 'form-input__label--required' : '';
    }
  },
  // detect changes in store step data
  watch: {
    value(newVal) {
      if(this.model !== newVal) this.model = newVal;
    }
  },
  mounted() {
    this.model = this.value;
  },
  methods: {
    /**
    * update model and validate field
    * @event onBlur
    * @type {Blur}
    */
    onBlur(event) {
      if(this.model !== null) {
        this.$validator.validate(this.id);
      }
      this.$emit('onchange', {
        key: this.id,
        val: this.model
      });

      this.$emit('onblur', event);
      this.isFocus = false;
    },

    /**
    * update model
    * @event onInput
    * @type {Input}
    */
    onInput() {
      this.$emit('oninput', this.model);
    },

    /**
    * update model
    * @event onFocus
    * @type {Focus}
    */
    onFocus() {
      this.$emit('onfocus');
      this.isFocus = true;
    }
  },
};

</script>

<style lang="scss">
  @import './FormInput';
</style>
